import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Animated,
  ColorValue,
  LayoutChangeEvent,
  PanResponder,
  Platform,
  StyleProp,
  StyleSheet,
  View,
  ViewStyle
} from 'react-native'
import { Path, Rect, RectProps, Svg } from 'react-native-svg'

import Button, { Props as CommonButtonProps } from '@common/components/Button'
import useTheme from '@common/hooks/useTheme'
import { BORDER_RADIUS_PRIMARY } from '@common/styles/utils/common'

type Props = Omit<CommonButtonProps, 'style' | 'children' | 'childrenPosition' | 'onPress'> & {
  style?: StyleProp<ViewStyle>
  onHoldComplete: () => void
  holdDuration?: number // in milliseconds
  holdText?: string
  completeText?: string
  buttonType?: 'primary' | 'error' | 'warning'
}

// The progress ring is stroked *inside* the button bounds (inset by half the
// stroke width), so it never gets clipped by the parent and doesn't need the
// button to reserve extra padding for it.
const OUTLINE_STROKE_WIDTH = 2

// The label/icon of a filled button is always white, so the ring rides on the
// same palette instead of a theme token (which would be invisible on `primary`
// in light mode).
const OUTLINE_TRACK_COLOR = 'rgba(255, 255, 255, 0.25)'
const OUTLINE_PROGRESS_COLOR = '#FFFFFF'

// iOS Safari treats a long-press as a text-selection gesture: it selects the
// button label and pops the native Copy callout, which interrupts the hold.
// Opt the whole button (label included — `user-select: auto` resolves against
// the parent, `-webkit-touch-callout` inherits) out of both. Safari only
// honors the -webkit- prefixed user-select, hence the explicit pair.
const WEB_NO_SELECT_STYLE =
  Platform.OS === 'web'
    ? ({
        userSelect: 'none',
        WebkitUserSelect: 'none',
        WebkitTouchCallout: 'none'
      } as unknown as ViewStyle)
    : null

// `Animated` tacks a `collapsable` prop onto whatever it drives (a no-op hint
// for the native view flattener). react-native-svg's web build forwards every
// unknown prop straight onto the DOM node, so it has to be dropped here or
// React warns about it on every frame of the hold.
const RectWithoutCollapsable = React.forwardRef<Rect, RectProps & { collapsable?: boolean }>(
  ({ collapsable, ...props }, ref) => <Rect ref={ref} {...props} />
)

const AnimatedRect = Animated.createAnimatedComponent(RectWithoutCollapsable)

/** Perimeter of a rounded rect — the dash length the progress stroke sweeps. */
const getRoundedRectPerimeter = (width: number, height: number, radius: number) =>
  2 * (width + height) - 8 * radius + 2 * Math.PI * radius

/**
 * Stroked tick, drawn inline rather than pulled from `assets/svg` because it
 * has to inherit the button's (white) label color — the shared CheckIcon fills
 * its own circle with `successDecorative`.
 */
const HoldCheckIcon: FC<{ color?: ColorValue }> = ({ color = OUTLINE_PROGRESS_COLOR }) => (
  <Svg width={18} height={18} viewBox="0 0 20 20" fill="none">
    <Path
      d="m4 10.5 3.7 3.7L16 5.8"
      stroke={color as string}
      strokeWidth={2.4}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
)

const HoldToProceedButton: FC<Props> = ({
  style,
  text = 'Hold to proceed',
  holdText = 'Sure?',
  completeText = 'Proceed',
  onHoldComplete,
  holdDuration = 1600,
  disabled,
  buttonType = 'primary',
  ...rest
}) => {
  const { theme } = useTheme()
  const progressAnim = useRef(new Animated.Value(0)).current
  const scaleAnim = useRef(new Animated.Value(1)).current
  // Fades the progress ring in on hold and out on cancel, independently of the
  // progress itself (which snaps back to 0 at a different speed).
  const outlineAnim = useRef(new Animated.Value(0)).current
  // Slides + fades the tick in once the hold completes.
  const iconAnim = useRef(new Animated.Value(0)).current
  const [isHolding, setIsHolding] = useState(false)
  const [isCompleted, setIsCompleted] = useState(false)
  const [size, setSize] = useState({ width: 0, height: 0 })
  const holdTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const animationRef = useRef<Animated.CompositeAnimation | null>(null)
  const holdStartTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const isCurrentlyHoldingRef = useRef(false)

  const colorTypes = {
    primary: theme.primary,
    error: theme.errorText,
    warning: theme.warningText
  }

  const startHold = useCallback(() => {
    if (disabled) return

    // Scale down animation for immediate visual feedback
    Animated.timing(scaleAnim, {
      toValue: 0.98,
      duration: 100,
      useNativeDriver: true
    }).start()

    // Wait 200ms before starting the hold animation to distinguish from quick clicks
    holdStartTimeoutRef.current = setTimeout(() => {
      setIsHolding(true)
      isCurrentlyHoldingRef.current = true

      Animated.timing(outlineAnim, {
        toValue: 1,
        duration: 160,
        useNativeDriver: false
      }).start()

      // Progress animation
      animationRef.current = Animated.timing(progressAnim, {
        toValue: 1,
        duration: holdDuration,
        useNativeDriver: false
      })

      animationRef.current.start(({ finished }) => {
        if (finished && isCurrentlyHoldingRef.current) {
          setIsCompleted(true)
          Animated.spring(iconAnim, {
            toValue: 1,
            friction: 7,
            tension: 90,
            useNativeDriver: false
          }).start()
          // Add a small delay before calling completion handler
          holdTimeoutRef.current = setTimeout(() => {
            onHoldComplete()
            // Don't reset the button after completion - keep it in completed state
          }, 300)
        }
      })
    }, 200)
  }, [disabled, holdDuration, progressAnim, scaleAnim, outlineAnim, iconAnim, onHoldComplete])

  const endHold = useCallback(() => {
    // Don't reset if already completed
    if (isCompleted) return

    // Mark that we're no longer holding
    isCurrentlyHoldingRef.current = false

    // Clear the hold start timeout if still waiting
    if (holdStartTimeoutRef.current) {
      clearTimeout(holdStartTimeoutRef.current)
      holdStartTimeoutRef.current = null
    }

    // If we're in the middle of holding, stop everything
    if (isHolding) {
      // Stop the progress animation immediately
      if (animationRef.current) {
        animationRef.current.stop()
        animationRef.current = null
      }

      // Clear the completion timeout
      if (holdTimeoutRef.current) {
        clearTimeout(holdTimeoutRef.current)
        holdTimeoutRef.current = null
      }
    }

    // Always reset progress animation to 0 and scale animation
    Animated.parallel([
      Animated.timing(progressAnim, {
        toValue: 0,
        duration: 150,
        useNativeDriver: false
      }),
      Animated.timing(outlineAnim, {
        toValue: 0,
        duration: 150,
        useNativeDriver: false
      }),
      Animated.timing(scaleAnim, {
        toValue: 1,
        duration: 100,
        useNativeDriver: true
      })
    ]).start()

    // Always reset the holding state
    setIsHolding(false)
    // Don't reset isCompleted here - let it stay true if the action completed
  }, [
    isHolding,
    isCompleted,
    animationRef,
    holdTimeoutRef,
    holdStartTimeoutRef,
    progressAnim,
    outlineAnim,
    scaleAnim
  ])

  const panResponder = useRef(
    PanResponder.create({
      onStartShouldSetPanResponder: () => !disabled,
      onStartShouldSetPanResponderCapture: () => !disabled,
      onMoveShouldSetPanResponder: () => true,
      onMoveShouldSetPanResponderCapture: () => false,
      onPanResponderGrant: startHold,
      onPanResponderMove: (_, gestureState) => {
        // If user moves too far from the button, end the hold
        const threshold = 50
        if (Math.abs(gestureState.dx) > threshold || Math.abs(gestureState.dy) > threshold) {
          endHold()
        }
      },
      onPanResponderRelease: endHold,
      onPanResponderTerminate: endHold,
      onPanResponderTerminationRequest: () => true
    })
  )

  // Update PanResponder when disabled state changes
  useEffect(() => {
    panResponder.current = PanResponder.create({
      onStartShouldSetPanResponder: () => !disabled,
      onStartShouldSetPanResponderCapture: () => !disabled,
      onMoveShouldSetPanResponder: () => true,
      onMoveShouldSetPanResponderCapture: () => false,
      onPanResponderGrant: startHold,
      onPanResponderMove: (_, gestureState) => {
        // If user moves too far from the button, end the hold
        const threshold = 50
        if (Math.abs(gestureState.dx) > threshold || Math.abs(gestureState.dy) > threshold) {
          endHold()
        }
      },
      onPanResponderRelease: endHold,
      onPanResponderTerminate: endHold,
      onPanResponderTerminationRequest: () => true
    })
  }, [disabled, startHold, endHold])

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (holdTimeoutRef.current) {
        clearTimeout(holdTimeoutRef.current)
      }
      if (holdStartTimeoutRef.current) {
        clearTimeout(holdStartTimeoutRef.current)
      }
      if (animationRef.current) {
        animationRef.current.stop()
      }
    }
  }, [])

  const onLayout = useCallback((e: LayoutChangeEvent) => {
    const { width, height } = e.nativeEvent.layout

    setSize((prev) => (prev.width === width && prev.height === height ? prev : { width, height }))
  }, [])

  // Callers style the wrapper (and, historically, the inner Button with the very
  // same object), so the ring has to follow whatever radius they set.
  const borderRadius = useMemo(() => {
    const flattened = StyleSheet.flatten(style) || {}

    return typeof flattened.borderRadius === 'number'
      ? flattened.borderRadius
      : BORDER_RADIUS_PRIMARY
  }, [style])

  // Calculate button text based on state
  const buttonText = (() => {
    if (isCompleted) return completeText
    if (isHolding) return holdText
    return text
  })()

  // Calculate progress width
  const progressWidth = progressAnim.interpolate({
    inputRange: [0, 1],
    outputRange: ['0%', '100%'],
    extrapolate: 'clamp'
  })

  // Progress bar background color - using theme colors for consistency
  const progressColor = isCompleted
    ? theme.successDecorative
    : isHolding
      ? colorTypes[buttonType]
      : 'transparent'

  const outline = useMemo(() => {
    if (!size.width || !size.height) return null

    const rectWidth = size.width - OUTLINE_STROKE_WIDTH
    const rectHeight = size.height - OUTLINE_STROKE_WIDTH
    if (rectWidth <= 0 || rectHeight <= 0) return null

    const radius = Math.min(borderRadius, rectWidth / 2, rectHeight / 2)
    const perimeter = getRoundedRectPerimeter(rectWidth, rectHeight, radius)
    const rectProps = {
      x: OUTLINE_STROKE_WIDTH / 2,
      y: OUTLINE_STROKE_WIDTH / 2,
      width: rectWidth,
      height: rectHeight,
      rx: radius,
      ry: radius,
      fill: 'none',
      strokeWidth: OUTLINE_STROKE_WIDTH
    }

    return (
      <Animated.View
        pointerEvents="none"
        style={[StyleSheet.absoluteFillObject, { opacity: outlineAnim, zIndex: 11 }]}
      >
        <Svg width={size.width} height={size.height}>
          <Rect {...rectProps} stroke={OUTLINE_TRACK_COLOR} />
          <AnimatedRect
            {...rectProps}
            stroke={
              isCompleted ? (theme.successDecorative as string) : OUTLINE_PROGRESS_COLOR
            }
            strokeLinecap="round"
            strokeDasharray={[perimeter, perimeter]}
            strokeDashoffset={progressAnim.interpolate({
              inputRange: [0, 1],
              outputRange: [perimeter, 0],
              extrapolate: 'clamp'
            })}
          />
        </Svg>
      </Animated.View>
    )
  }, [size, borderRadius, outlineAnim, progressAnim, isCompleted, theme.successDecorative])

  return (
    <Animated.View
      onLayout={onLayout}
      style={[
        {
          position: 'relative',
          transform: [{ scale: scaleAnim }]
        },
        WEB_NO_SELECT_STYLE,
        style
      ]}
      {...panResponder.current.panHandlers}
    >
      <Button
        style={[
          {
            minWidth: 160,
            position: 'relative',
            backgroundColor: colorTypes[buttonType]
          },
          style
        ]}
        hasBottomSpacing={false}
        text={buttonText}
        disabled={disabled}
        type={buttonType}
        childrenPosition="left"
        // Collapsed to zero width until the hold completes, so the tick slides
        // out of the label instead of shifting it on mount.
        childrenContainerStyle={
          {
            overflow: 'hidden',
            opacity: iconAnim,
            width: iconAnim.interpolate({
              inputRange: [0, 1],
              outputRange: [0, 18],
              extrapolate: 'clamp'
            }),
            marginRight: iconAnim.interpolate({
              inputRange: [0, 1],
              outputRange: [0, 8],
              extrapolate: 'clamp'
            })
          } as any
        }
        {...rest}
      >
        <HoldCheckIcon />
      </Button>

      {/* Clips the sweeping fill to the button's rounded corners. Kept separate
          from the wrapper so the wrapper can stay `overflow: visible` for the
          ring. */}
      <View
        pointerEvents="none"
        style={[StyleSheet.absoluteFillObject, { overflow: 'hidden', borderRadius, zIndex: 10 }]}
      >
        <Animated.View
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            height: '100%',
            width: progressWidth,
            backgroundColor: progressColor,
            opacity: 0.3
          }}
        />
      </View>

      {outline}
    </Animated.View>
  )
}

export default HoldToProceedButton
